import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { railStore } from '@/stores/RailStore';
import { entityStore } from '@/stores/EntityStore';
import { Badge } from '@react/components';
import type { RailTab } from '@/features/layout/types';
import { groupResourcesByRelation, entityToRoute } from '@/features/resources/logic';
import { unlinkResources } from '@/features/resources/api';
import { ResourceManagePanel } from '@/features/resources/ResourceManagePanel';
import type { ResourceCardData } from '@/features/resources/types';
import type { AspectCardData } from '@/features/aspects/types';
import { toAspectCardData } from '@/features/aspects/logic';
import { ResourceList } from '@/components/ResourceList';
import { ResourceCommandMenu } from '@/components/ResourceCommandMenu';
import { AspectPanel } from '@/components/AspectPanel';
import { AspectCommandMenu } from '@/components/AspectCommandMenu';
import { ServiceRailPanel } from '@/components/services/ServiceRailPanel';
import type { Resource } from '@core/types/resource';
import type { Aspect } from '@core/types/task/Aspect';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function BoxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

const TABS: { id: RailTab; icon: React.FC<{ className?: string }>; labelKey: string }[] = [
  { id: 'actions', icon: BoltIcon, labelKey: 'layout.rail.actions' },
  { id: 'resources', icon: LinkIcon, labelKey: 'layout.rail.resources' },
  { id: 'aspects', icon: LayersIcon, labelKey: 'layout.rail.aspects' },
  { id: 'services', icon: BoxIcon, labelKey: 'layout.rail.services' },
  { id: 'ai', icon: SparklesIcon, labelKey: 'layout.rail.ai' },
];

export const RightRail = observer(() => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const [resourceMenuOpen, setResourceMenuOpen] = useState(false);
  const [resourceMenuRelationType, setResourceMenuRelationType] = useState<string | undefined>();
  const [aspectMenuOpen, setAspectMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging.current) return;
    const delta = startX.current - e.clientX;
    railStore.resize(startWidth.current + delta);
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = railStore.width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }, [onPointerMove, onPointerUp]);

  // --- Handlers ---
  const handleNavigateToResource = (data: ResourceCardData) => {
    const route = entityToRoute({ type_id: data.typeId, id: data.id });
    if (route) navigate(route);
  };

  const handleUnlinkResource = (resourceId: string) => {
    const current = entityStore.currentEntity;
    const resource = entityStore.resources.find((r) => r.entity?.id === resourceId);
    // Optimistic remove; restore on failure since the request is fire-and-forget from the UI's view.
    entityStore.removeResourceOptimistic(resourceId);
    if (current && resource?.urn) {
      unlinkResources(
        { urn: current.urn } as any,
        { urn: resource.urn } as any,
        resource.relation_type,
      ).catch(() => {
        entityStore.addResourceOptimistic(resource);
      });
    }
  };

  const handleResourceLinked = (resource: Resource) => {
    entityStore.addResourceOptimistic(resource);
  };

  const handleDetachAspect = (aspectId: string) => {
    entityStore.removeAspectOptimistic(aspectId);
  };

  const handleAspectAttached = (aspect: Aspect) => {
    entityStore.addAspectOptimistic(aspect);
  };

  const handleNavigateToAspect = (data: AspectCardData) => {
    const route = entityToRoute({ type_id: data.typeId, id: data.id });
    if (route) navigate(route);
  };

  // Public method for opening resource menu with preset relation type (used by shortcuts)
  const openResourceMenu = (relationType?: string) => {
    setResourceMenuRelationType(relationType);
    setResourceMenuOpen(true);
  };

  // Expose for keyboard shortcuts
  (window as any).__railOpenResourceMenu = openResourceMenu;

  // Nothing to show means nothing to open. The toggle is hidden too, so this
  // is the safety net for deep links and stale state, not the main gate.
  if (!railStore.isOpen || !railStore.hasContent) return null;

  const badgeForTab = (tab: RailTab): number => {
    if (tab === 'resources') return entityStore.totalResources;
    if (tab === 'aspects') return entityStore.totalAspects;
    return 0;
  };

  const aspectCardData = entityStore.aspects.map(toAspectCardData);
  const attachedTypeNames = aspectCardData.map((a) => a.typeName);

  // A tab exists only when its source exists: quick actions come from the page,
  // everything else from the entity in context. Empty tabs are not offered.
  const visibleTabs = TABS.filter((tab) =>
    tab.id === 'actions' ? railStore.pageActions.length > 0 : entityStore.currentEntity !== null,
  );
  const activeTab = visibleTabs.some((tab) => tab.id === railStore.activeTab)
    ? railStore.activeTab
    : visibleTabs[0]?.id;

  const tabBar = (
    <div className="flex items-center h-12 px-2 border-b border-rail-border flex-shrink-0">
      <div className="flex items-center gap-0.5 flex-1">
        {visibleTabs.map((tab) => {
          const active = activeTab === tab.id;
          const count = badgeForTab(tab.id);
          return (
            <button
              key={tab.id}
              onClick={() => railStore.setTab(tab.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                active
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-rail-foreground hover:bg-accent/50'
              }`}
              aria-pressed={active}
            >
              <tab.icon className="h-3.5 w-3.5" />
              <span className="sm:inline">{t(tab.labelKey)}</span>
              {count > 0 && (
                <Badge variant="default" size="sm">{count}</Badge>
              )}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => railStore.close()}
        className="p-1 rounded-md text-muted-foreground hover:text-rail-foreground hover:bg-accent/50 transition-colors"
        aria-label={t('layout.rail.close')}
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );

  const actionsPanel = (
    <div className="space-y-1">
      {railStore.pageActions.map((action) => (
        <button
          key={action.key}
          type="button"
          onClick={action.onSelect}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-rail-foreground hover:bg-accent/50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent/40 text-muted-foreground" aria-hidden="true">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
            </svg>
          </span>
          {action.label}
        </button>
      ))}
    </div>
  );

  const tabContent = (
    <div className="flex-1 overflow-y-auto p-3">
      {activeTab === 'actions' && railStore.pageActions.length > 0 ? (
        actionsPanel
      ) : !entityStore.currentEntity ? (
        <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
          {t('layout.rail.noContext')}
        </div>
      ) : (
        <>
          {activeTab === 'resources' && (
            <ResourceList
              groups={groupResourcesByRelation(entityStore.resources)}
              isLoading={entityStore.isLoadingResources}
              onUnlink={handleUnlinkResource}
              onNavigate={handleNavigateToResource}
              onAddClick={() => setResourceMenuOpen(true)}
              onManageClick={() => setManageOpen(true)}
            />
          )}
          {activeTab === 'aspects' && (
            <AspectPanel
              aspects={aspectCardData}
              isLoading={entityStore.isLoadingAspects}
              onDetach={handleDetachAspect}
              onNavigate={handleNavigateToAspect}
              onAddClick={() => setAspectMenuOpen(true)}
            />
          )}
          {activeTab === 'services' && (
            <ServiceRailPanel />
          )}
          {activeTab === 'ai' && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-rail-foreground">
                {t('layout.rail.ai')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('layout.rail.aiPlaceholder')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile / Tablet: drawer overlay */}
      <div className="xl:hidden fixed inset-0 z-40">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={() => railStore.close()}
          aria-hidden="true"
        />

        {/* Drawer panel — slides in from right */}
        <aside
          aria-label={t('layout.rail.label')}
          className="absolute inset-y-0 right-0 w-80 max-w-[calc(100vw-3rem)] bg-rail border-l border-rail-border shadow-lg flex flex-col animate-in slide-in-from-right duration-200"
        >
          {tabBar}
          {tabContent}
        </aside>
      </div>

      {/* Desktop: inline panel */}
      <aside
        aria-label={t('layout.rail.label')}
        className="relative hidden xl:flex flex-col h-full bg-rail border-l border-rail-border z-20"
        style={{ width: railStore.width }}
      >
        {/* Resize handle */}
        <div
          onPointerDown={onResizeStart}
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors z-30"
        />
        {tabBar}
        {tabContent}
      </aside>

      {/* Command menus (portaled above everything) */}
      <ResourceCommandMenu
        isOpen={resourceMenuOpen}
        onClose={() => { setResourceMenuOpen(false); setResourceMenuRelationType(undefined); }}
        entityUrn={entityStore.currentEntity?.urn || ''}
        entityType={entityStore.currentEntity?.type || ''}
        defaultRelationType={resourceMenuRelationType}
        onLinked={handleResourceLinked}
      />
      <AspectCommandMenu
        isOpen={aspectMenuOpen}
        onClose={() => setAspectMenuOpen(false)}
        entityType={entityStore.currentEntity?.type || ''}
        attachedTypeNames={attachedTypeNames}
        onAttached={handleAspectAttached}
      />
      {manageOpen && entityStore.currentEntity && (
        <ResourceManagePanel
          entity={entityStore.currentEntity}
          onClose={() => setManageOpen(false)}
          onChanged={() => entityStore.loadResources(entityStore.currentEntity!)}
        />
      )}
    </>
  );
});
